import {ContentEditableHtmlEditor} from '@frontapp/editor-html';
import React, {FC, RefCallback, useCallback} from 'react';
import styled from 'styled-components';

import {LinkTrap} from '../../../../../libs/shared/ui/src/components/links/linkTrap';
import {LinkTooltips} from '../../../../../libs/shared/ui/src/components/tooltips/linkTooltips';
import {useEditorDarkMode} from '../../../../../libs/shared/ui/src/styles/darkModeEditorHelpers';
import {
  growToFitHeightContainerStyle,
  growToFitHeightStyle,
} from '../../../../../libs/shared/ui/src/styles/styleHelpers';
import {ContentEditable, EditorTypesEnum} from '../../../common/editor/contentEditable';
import {EmailComposerEditorBase, EmailComposerEditorBaseProps} from './emailComposerEditorBase';

/*
 * Props.
 */

export interface EmailComposerHtmlEditorProps extends Partial<EmailComposerEditorBaseProps> {
  className?: string;
  refCallback?: RefCallback<HTMLDivElement>;
  editor: ContentEditableHtmlEditor;
  shouldFocus?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  onPaste?: () => void;
}

/*
 * Style.
 */

const StyledLinkTooltips = styled(LinkTooltips)`
  ${growToFitHeightContainerStyle};
`;

const StyledLinkTrap = styled(LinkTrap)`
  ${growToFitHeightStyle};
  ${growToFitHeightContainerStyle};
`;

/*
 * Component.
 */

export const EmailComposerHtmlEditor: FC<EmailComposerHtmlEditorProps> = ({
  className,
  refCallback,
  editor,
  shouldFocus,
  onFocus,
  onBlur,
  onPaste,
  ...props
}) => {
  const darkModeRefCallback = useEditorDarkMode();
  const refCallbackWrapper = useCallback<RefCallback<HTMLDivElement>>(
    (element) => {
      darkModeRefCallback(element);

      if (refCallback) {
        refCallback(element);
      }
    },
    [darkModeRefCallback, refCallback],
  );

  return (
    <StyledLinkTooltips className={className} showKeyboardShortcut>
      <StyledLinkTrap requireMetaKey>
        <EmailComposerEditorBase {...props}>
          {(editorProps) => (
            <ContentEditable
              {...editorProps}
              editor={editor}
              shouldFocus={shouldFocus}
              onFocus={onFocus}
              onBlur={onBlur}
              onPaste={onPaste}
              editorType={EditorTypesEnum.EMAIL}
              refCallback={refCallbackWrapper}
            />
          )}
        </EmailComposerEditorBase>
      </StyledLinkTrap>
    </StyledLinkTooltips>
  );
};
